import * as React from 'react';
import { CaptionAccentTypography, H4Typography } from 'ui';
import { sampleLogo } from './sample-logo';
import type { LogoSample } from './sample-logo';
import { cn } from '@utils';

export type BrandLogo = {
	/** Trimmed, sized image to display. */
	url: string;
	/** Small untrimmed thumbnail whose corners reveal the logo's own background. */
	sampleUrl: string;
};

type Props = {
	title: string;
	subtitle?: string;
	/** Organization logo shown in place of the visible title. Falls back to the text header when missing or when the image fails to load; a different url is retried. */
	logo?: BrandLogo;
};

type SampledLogo = LogoSample & { url: string };

/**
 * Login / signup card header. Renders the org logo above the subtitle when one is provided and loads,
 * otherwise the text title above the subtitle. The logo sits in a padded box and is kept invisible until its
 * untrimmed thumbnail has been sampled, so it paints once at its final size. A logo with its own background gets
 * that color extended as padding with rounded corners on the box; a mostly-white logo gets a tinted chip; a photo
 * (opaque, no background) is rounded itself. Nothing else is ever clipped. The logo image is decorative, so the
 * heading stays in the DOM, visually hidden, to keep one accessible title reachable by heading navigation.
 */
export const BrandHeader: React.FC<Props> = ({ title, subtitle, logo }) => {
	const [failedLogoUrl, setFailedLogoUrl] = React.useState<string>();
	const [sampledLogo, setSampledLogo] = React.useState<SampledLogo>();

	const showLogo = Boolean(logo) && failedLogoUrl !== logo?.url;
	const sample = sampledLogo && sampledLogo.url === logo?.url ? sampledLogo : undefined;
	const frameColor = sample?.backgroundColor;
	const isLight = Boolean(sample?.isLight);
	const isPhoto = Boolean(sample?.isOpaque) && !frameColor;
	const isFramed = isLight || Boolean(frameColor);

	const logoUrl = logo?.url;
	const sampleUrl = logo?.sampleUrl;
	React.useEffect(() => {
		if (!logoUrl || !sampleUrl) {
			return undefined;
		}
		let cancelled = false;
		void sampleLogo(sampleUrl).then(result => {
			if (!cancelled) {
				setSampledLogo({ isLight: false, isOpaque: false, ...result, url: logoUrl });
			}
		});
		return () => {
			cancelled = true;
		};
	}, [logoUrl, sampleUrl]);

	return (
		<header
			className={cn(
				'flex max-w-[40rem] flex-col items-center self-center text-center',
				showLogo ? 'gap-[0.8rem]' : 'gap-1'
			)}
			data-testid="auth-brand-header"
		>
			{showLogo && logo && (
				<div
					className={cn(
						'flex h-[7.2rem] items-center justify-center px-[1.2rem] md:h-[8rem]',
						isFramed && 'rounded-[1.2rem]',
						isLight && 'bg-borderSeparator'
					)}
					style={frameColor ? { backgroundColor: frameColor } : undefined}
					data-testid="auth-brand-header-logo-box"
				>
					<img
						src={logo.url}
						alt=""
						className={cn(
							'block h-auto max-h-[7.2rem] w-auto max-w-[16rem] object-contain md:max-h-[8rem] md:max-w-[20rem]',
							!sample && 'invisible',
							isFramed && 'max-h-[5.6rem] md:max-h-[6.4rem]',
							isPhoto && 'rounded-[1.2rem]'
						)}
						onError={() => setFailedLogoUrl(logo.url)}
						data-testid="auth-brand-header-logo"
					/>
				</div>
			)}
			<H4Typography className={showLogo ? 'sr-only' : 'text-center text-primary'} data-testid="auth-brand-header-title">
				{title}
			</H4Typography>
			{subtitle && (
				<CaptionAccentTypography className="text-center text-primary" truncate={false}>
					{subtitle}
				</CaptionAccentTypography>
			)}
		</header>
	);
};
